import { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Download } from 'lucide-react';
import PageHeader from '@/components/PageHeader.jsx';
import { descargarResumenGeneralCuentaPdf } from '@/services/clientes.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { canAccessCustomerDebt } from '@/security/customerDebt.js';

async function getGeneralReportErrorMessage(error) {
  const responseData = error?.response?.data;
  if (responseData && typeof responseData === 'object') {
    try {
      let body = responseData;
      if (typeof responseData.text === 'function') body = await responseData.text();
      else if (typeof FileReader !== 'undefined' && typeof Blob !== 'undefined' && responseData instanceof Blob) {
        body = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(reader.error);
          reader.readAsText(responseData);
        });
      }
      const payload = typeof body === 'string' ? JSON.parse(body) : body;
      if (payload?.message || payload?.error) return payload.message || payload.error;
    } catch {
      // Fall through to a status-specific message when the response isn't JSON.
    }
  }
  if (error?.response?.status === 403) return 'No tenés permisos para generar el PDF.';
  if (error?.response?.status === 401) return 'Tu sesión venció. Volvé a iniciar sesión e intentá nuevamente.';
  return 'No se pudo generar el resumen general. Revisá la conexión e intentá nuevamente.';
}

export default function CustomerDebtPage() {
  const { user, tenant, sucursalActiva, sucursalPrincipal, sucursalesHabilitadas = [], can } = useAuth();
  const activeBranchId = String(sucursalActiva?.id || sucursalActiva?.sucursal_id || sucursalPrincipal?.id || sucursalPrincipal?.sucursal_id || '').trim();
  const canViewGlobalBranches = Boolean(can?.('CUENTA_CORRIENTE_VER_GLOBAL') || can?.('SUCURSAL_OVERRIDE'));
  const branchOptions = useMemo(() => {
    const unique = new Map();
    [sucursalActiva, sucursalPrincipal, ...sucursalesHabilitadas].forEach((branch) => {
      const id = String(branch?.id || branch?.sucursal_id || '').trim();
      if (id && (canViewGlobalBranches || id === activeBranchId)) unique.set(id, { id, nombre: branch?.nombre || `Sucursal #${id}` });
    });
    return Array.from(unique.values()).sort((left, right) => left.nombre.localeCompare(right.nombre, 'es'));
  }, [sucursalActiva, sucursalPrincipal, sucursalesHabilitadas, canViewGlobalBranches, activeBranchId]);
  const [generalFilters, setGeneralFilters] = useState({ desde: '', hasta: '', sucursalId: '', saldo: 'deudores' });
  const [generalDownloading, setGeneralDownloading] = useState(false);
  const [generalError, setGeneralError] = useState('');

  useEffect(() => {
    if (!activeBranchId) return;
    setGeneralFilters((current) => current.sucursalId ? current : { ...current, sucursalId: activeBranchId });
  }, [activeBranchId]);

  const downloadGeneralStatement = async () => {
    if (!canAccessCustomerDebt(user, tenant)) return;
    if (generalFilters.desde && generalFilters.hasta && generalFilters.desde > generalFilters.hasta) {
      setGeneralError('La fecha desde no puede ser posterior a la fecha hasta.');
      return;
    }
    setGeneralDownloading(true);
    setGeneralError('');
    try {
      const file = await descargarResumenGeneralCuentaPdf(generalFilters);
      const url = URL.createObjectURL(file.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.filename;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      setGeneralError(await getGeneralReportErrorMessage(error));
    } finally {
      setGeneralDownloading(false);
    }
  };

  if (!canAccessCustomerDebt(user, tenant)) return <p role="alert" className="p-4 text-sm">No tiene acceso al informe de deuda de clientes.</p>;
  return <div className="w-full min-w-0 space-y-3">
    <PageHeader title="Deuda de Clientes" subtitle="Consulta y descarga del informe general de deuda de clientes" />
      <section className="rounded-2xl border border-[rgba(var(--border),.55)] bg-[rgba(var(--panel),.9)] p-4 shadow-sm">
        <p className="text-sm text-[rgb(var(--fg))]">Este informe incluye los comprobantes pendientes y ajustes de los clientes con saldo deudor actual (y, si se elige, los clientes con saldo a favor) comprendidos en los filtros y permisos aplicables. No está limitado a un cliente individual.</p>
        <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end">
          <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4 lg:flex-1">
            <label className="text-xs font-semibold text-[rgb(var(--muted))]">Emisión desde
              <span className="relative mt-1 block"><CalendarDays size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2" /><input type="date" value={generalFilters.desde} onChange={(event) => setGeneralFilters((current) => ({ ...current, desde: event.target.value }))} className="h-10 w-full rounded-lg border bg-[rgb(var(--bg))] pl-8 pr-2 text-sm text-[rgb(var(--fg))]" /></span>
            </label>
            <label className="text-xs font-semibold text-[rgb(var(--muted))]">Emisión hasta
              <span className="relative mt-1 block"><CalendarDays size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2" /><input type="date" value={generalFilters.hasta} onChange={(event) => setGeneralFilters((current) => ({ ...current, hasta: event.target.value }))} className="h-10 w-full rounded-lg border bg-[rgb(var(--bg))] pl-8 pr-2 text-sm text-[rgb(var(--fg))]" /></span>
            </label>
            <label className="text-xs font-semibold text-[rgb(var(--muted))]">Sucursal
              <select value={generalFilters.sucursalId} onChange={(event) => setGeneralFilters((current) => ({ ...current, sucursalId: event.target.value }))} className="mt-1 h-10 w-full rounded-lg border bg-[rgb(var(--bg))] px-2 text-sm text-[rgb(var(--fg))]">
                {canViewGlobalBranches ? <option value="">Todas las sucursales disponibles</option> : null}
                {branchOptions.map((branch) => <option key={branch.id} value={branch.id}>{branch.nombre}</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold text-[rgb(var(--muted))]">Clientes a incluir
              <select value={generalFilters.saldo} onChange={(event) => setGeneralFilters((current) => ({ ...current, saldo: event.target.value }))} className="mt-1 h-10 w-full rounded-lg border bg-[rgb(var(--bg))] px-2 text-sm text-[rgb(var(--fg))]">
                <option value="deudores">Solo deudores</option>
                <option value="todos">Deudores y con saldo a favor</option>
              </select>
            </label>
          </div>
          <button type="button" onClick={downloadGeneralStatement} disabled={generalDownloading} className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-4 text-sm font-semibold text-[#161b23] shadow-sm transition hover:brightness-105 disabled:cursor-wait disabled:opacity-60">
            <Download size={16} />{generalDownloading ? 'Generando PDF…' : 'Descargar informe PDF'}
          </button>
        </div>
        <p className="mt-3 text-xs text-[rgb(var(--muted))]">El período seleccionado filtra la fecha de emisión de los comprobantes. Los saldos reflejan los pagos registrados posteriormente.</p>
        {generalError ? <p role="alert" className="mt-3 rounded-lg border border-rose-500/25 bg-rose-500/5 px-3 py-2 text-xs text-rose-700 dark:text-rose-300">{generalError}</p> : null}
      </section>
  </div>;
}
